import CompoundedLanding from '../../.shared/CompoundedLanding/CompoundedLanding';
import { trackPostHogEvent } from '../../../utils/posthogUtils';
import heroImage from '../../../assets/hero-runner-city.webp';
import vialImage from '../../../assets/nad-vial.png';

// External NAD+ medical intake. NAD+ does not go through the GLP-1 /interview funnel.
const NAD_INTAKE_URL = 'https://patient-portal.tb2.health/dashboard?nad=monthly';

const HERO = {
    image: heroImage,
    imageAlt: 'Woman jogging down a city street',
    // A landscape photo, so it fills the panel and is cropped rather than fitted.
    fillImage: true,
    eyebrow: 'NAD+ Care',
    title: 'Clinician-Reviewed NAD+',
    sub: 'Compounded NAD+ · Personalized, clinician-guided program',
    subSmall: 'Start in minutes with transparent, all-inclusive pricing. If prescribed, clinician-directed dose levels and adjustments are included within your program.',
    ctaLabel: 'Subscribe Now',
};

const TABS = [
    {
        id: 'included',
        label: "What's included",
        body: 'All-inclusive program pricing includes clinician evaluation and ongoing care. If prescribed, program pricing also covers medication, supplies, and shipping.',
    },
    {
        id: 'shipping',
        label: 'How it ships',
        body: 'If prescribed, your medication is dispensed by a state-licensed pharmacy. Follow pharmacy storage and handling instructions provided with your medication.',
    },
    {
        id: 'providers',
        label: 'Provider roles',
        body: 'Platform facilitates care. Clinicians prescribe independently. Pharmacies dispense medications. Prescriptions are not guaranteed. Treatment decisions are made solely by a licensed clinician based on your intake.',
    },
];

const LOCKUP = {
    image: vialImage,
    // Wider than the semaglutide vial, so it is drawn smaller to fit the band.
    smallImage: true,
    imageAlt: 'tb2.health compounded NAD+ vial',
    eyebrow: 'Compounded NAD+ Care',
    accent: 'NAD+',
    sentences: [
        {
            lead: 'What it is.',
            text: 'NAD+ (nicotinamide adenine dinucleotide) is a coenzyme naturally present in every cell in the body. It helps cells convert nutrients into usable energy and is involved in cellular metabolism and DNA repair.',
        },
        {
            lead: "How it's commonly used.",
            text: 'NAD+ is commonly used in wellness programs and is being studied for its role in cellular energy, metabolic function, and healthy aging. These uses are not FDA-approved, and your clinician will determine whether it is appropriate for you.',
        },
    ],
};

const HOW_IT_WORKS = {
    subtitle: 'Remote clinician evaluation available in eligible states where permitted by law.',
    steps: [
        { num: 'Step 1', title: 'Submit your intake', body: 'Complete your intake from your phone in just a few minutes. See pricing and subscription terms before you begin.', tag: '5 minutes' },
        { num: 'Step 2', title: 'Clinician review', body: 'Reviewed by a licensed clinician. Prescriptions are not guaranteed.', tag: 'Within 24 hours' },
        { num: 'Step 3', title: 'If prescribed, fulfillment', body: 'If appropriate, your prescription is sent to a state-licensed pharmacy, which dispenses and ships your medication.', tag: 'Free shipping', featured: true },
        { num: 'Step 4', title: 'Ongoing support', body: 'Ongoing care with clinician oversight and tolerability monitoring. Dose adjustments are included.', tag: 'Cancel anytime' },
    ],
};

const RESEARCH = {
    eyebrow: 'For educational purposes only',
    title: 'What researchers are studying',
    levels: [
        {
            id: 'simple',
            label: 'Plain language',
            note: 'NAD⁺ (nicotinamide adenine dinucleotide) is a natural molecule found in every cell. Scientists are studying it because it plays a central role in how our bodies make energy, repair DNA, and stay resilient as we age. This is not medical advice.',
            headers: ['Goal / Focus Area', 'What Researchers Are Studying', 'Why It Might Matter'],
            rows: [
                { goal: 'Energy & Longevity', compound: 'NAD⁺ and its precursors (NMN, NR)', why: 'NAD⁺ helps cells make ATP — the body’s energy currency. Levels drop with age, which may affect energy and cellular health.' },
                { goal: 'DNA Repair', compound: 'NAD⁺ and enzymes called PARPs', why: 'PARPs use NAD⁺ to fix everyday DNA damage caused by stress and metabolism, helping preserve genetic stability over time.' },
                { goal: 'Metabolic Balance', compound: 'NAD⁺ and sirtuin-related pathways', why: 'NAD⁺ supports proteins (sirtuins) that influence how the body handles sugar, fat, and overall metabolic efficiency.' },
                { goal: 'Brain & Nerve Support', compound: 'NAD⁺ and neuronal energy metabolism', why: 'Healthy NAD⁺ levels may help neurons manage oxidative stress and maintain sharp mental function as we age.' },
                { goal: 'Inflammation Control', compound: 'NAD⁺ and sirtuin activation', why: 'By fueling sirtuins, NAD⁺ may help calm chronic low-grade inflammation — a key factor in aging and immune regulation.' },
            ],
        },
        {
            id: 'tech',
            label: 'Technical',
            note: 'NAD⁺ (Nicotinamide Adenine Dinucleotide) is a critical metabolic cofactor studied for its role in cellular energy, DNA repair, and mitochondrial function. None of the information below constitutes medical advice.',
            headers: ['Goal / Area of Study', 'Compounds Under Investigation', 'Mechanistic Rationale (under study)'],
            rows: [
                { goal: 'Mitochondrial Energy & Longevity', compound: 'NAD⁺ · NMN · NR (precursors)', why: 'Central redox cofactor supporting ATP synthesis; cellular NAD⁺ decline linked to aging' },
                { goal: 'DNA Repair & Genomic Stability', compound: 'NAD⁺ · PARP activators', why: 'Fuels PARP-dependent DNA-repair pathways; supports genomic maintenance under oxidative stress' },
                { goal: 'Metabolic Regulation & Insulin Sensitivity', compound: 'NAD⁺ · Sirtuin modulators (SIRT1/3)', why: 'Investigated for improved glucose handling, lipid metabolism, and mitochondrial biogenesis' },
                { goal: 'Neuroprotection & Cognitive Health', compound: 'NAD⁺ · Sirtuin axis', why: 'Explored for maintaining neuronal energy balance and protecting against oxidative injury' },
                { goal: 'Inflammation & Immune Modulation', compound: 'NAD⁺ · SIRT1/3 pathway', why: 'Associated with reduced pro-inflammatory cytokine activity and enhanced stress-response signaling' },
            ],
        },
    ],
};

const SAFETY = {
    eyebrow: 'Treatment & safety',
    title: 'Important safety information',
    columns: [
        {
            title: 'Dosing & expectations',
            sub: 'Clinician-directed',
            paragraphs: ['Clinician determines dosing. If prescribed, clinician-directed dose adjustments are included within your program.'],
            strong: 'NAD+ has not been shown to be safe or effective for any condition. Response varies. No guaranteed outcomes.',
        },
        {
            title: 'Reported side effects',
            sub: 'May include',
            list: ['Chills', 'Shaking', 'Vomiting', 'Fatigue', 'Injection or infusion reactions'],
            note: 'The FDA has received adverse event reports after use of NAD+ injectable products, some requiring medical treatment.',
        },
        {
            title: 'Who should avoid',
            sub: 'Discuss with your clinician',
            paragraphs: ['Not recommended during pregnancy, breastfeeding, or when planning pregnancy. Not established as safe for individuals under 18. Older adults should discuss use carefully with their clinician.'],
        },
        {
            title: 'Monitoring & interactions',
            sub: 'Report any reaction promptly',
            paragraphs: [
                'Your clinician may monitor tolerability, blood pressure, heart rate, and other symptoms over time. Report any reaction promptly.',
                'Interaction data is limited. Disclose all medications, supplements, and conditions during your intake for review by a licensed clinician.',
            ],
        },
    ],
};

const FAQ_DESCRIPTION = "Everything you might want to know about compounded NAD+, dosing, billing, and what's included in your program.";

const FAQ_ITEMS = [
    { question: 'Is compounded NAD+ FDA-approved?', answer: 'No. NAD+ is not an FDA-approved drug and has no FDA-approved indication. Compounded NAD+ has not been evaluated by the FDA for safety or efficacy. A prescription is required.' },
    { question: 'What is NAD+?', answer: 'NAD+ (nicotinamide adenine dinucleotide) is a coenzyme naturally present in every cell in the body. It helps cells convert nutrients into usable energy and is involved in cellular metabolism and DNA repair.' },
    { question: 'How is dosing determined?', answer: 'Clinician determines dosing. If prescribed, clinician-directed dose adjustments are included within your program.' },
    { question: 'Can I take NAD+ with other medications, like a GLP-1?', answer: 'NAD+ is frequently paired with other treatments, including GLP-1 medications, in wellness and weight management programs. Interaction data is limited, so disclose all medications, supplements, and conditions during your intake for review by a licensed clinician.' },
    { question: 'How does billing work?', answer: 'Subscription renews monthly unless canceled. Future charges are disclosed before purchase. Full refund issued if a clinician determines treatment is not appropriate. Additional refund eligibility subject to policy.' },
    { question: 'How should I store the medication?', answer: 'Follow pharmacy storage and handling instructions provided with your medication.' },
    { question: 'Who prescribes and dispenses?', answer: 'Platform facilitates care. Clinicians prescribe independently. Pharmacies dispense medications.' },
];

const SMALLPRINT = [
    { title: 'Recurring terms', body: 'Subscription renews monthly unless canceled. Future charges are disclosed before purchase.' },
    { title: 'Refunds', body: 'Full refund issued if a clinician determines treatment is not appropriate. Additional refund eligibility subject to policy.' },
    { title: 'Authorization', body: 'By continuing, you agree to recurring billing, telehealth evaluation, and pharmacy fulfillment if prescribed.' },
];

export default function NadLanding() {
    const handleStartIntake = (selected) => {
        trackPostHogEvent('landing_page_submitted', {
            selected,
            to_path: NAD_INTAKE_URL,
            product: 'nad',
        });
        if (NAD_INTAKE_URL) {
            window.location.assign(NAD_INTAKE_URL);
        }
    };

    return (
        <CompoundedLanding
            hero={HERO}
            onCtaClick={handleStartIntake}
            price={{ product: 'nad', formulation: 'injectable' }}
            tabs={TABS}
            lockup={LOCKUP}
            howItWorks={HOW_IT_WORKS}
            research={RESEARCH}
            safety={SAFETY}
            faq={{
                items: FAQ_ITEMS,
                description: FAQ_DESCRIPTION,
                ctaLabel: 'Subscribe Now',
                onCtaClick: () => handleStartIntake('faq_get_started'),
            }}
            smallprint={SMALLPRINT}
        />
    );
}
